import { KeyboardEvent, useRef, useState } from "react";
import { Pencil } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";

interface EditableTitleProps {
  value: string;
  canEdit: boolean;
  // Persist the new name. Should throw on failure so the title reverts.
  onSave: (next: string) => Promise<void>;
  // Used for the input's aria-label and the "Edit <label>" button.
  label?: string;
  className?: string;
}

/**
 * Inline-editable heading shared by the project / dataset / agent overview
 * pages: a pencil appears on hover, clicking it swaps the title for an input,
 * and committing (Enter or clicking away) saves automatically. The caller's
 * `onSave` owns the API call + toast; throwing reverts the title.
 */
export function EditableTitle({
  value,
  canEdit,
  onSave,
  label = "name",
  className,
}: EditableTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  // Set when Escape cancels, so the resulting blur doesn't also commit.
  const skipCommitRef = useRef(false);

  const startEdit = () => {
    setDraft(value);
    setEditing(true);
  };

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === value) {
      setDraft(value);
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
    } catch {
      setDraft(value);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  // Enter/Escape both blur the input; onBlur is the single commit path so a
  // keypress can't double-fire with the unmount blur.
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      skipCommitRef.current = true;
      e.currentTarget.blur();
    }
  };

  const handleBlur = () => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false;
      setDraft(value);
      setEditing(false);
      return;
    }
    void commit();
  };

  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={saving}
        aria-label={label}
        className={cn("h-auto w-auto min-w-72 py-1 text-2xl font-bold", className)}
      />
    );
  }

  return (
    <div className="group inline-flex items-center gap-2">
      <h1 className={cn("text-2xl font-bold text-foreground", className)}>{value}</h1>
      {canEdit && (
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
          aria-label={`Edit ${label}`}
          onClick={startEdit}
        >
          <Pencil className="size-3.5" />
        </Button>
      )}
    </div>
  );
}
